import { useCallback, useState, useEffect, useRef } from 'react';
import { DateTime, Interval } from 'luxon';

import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import { DATE_MODIFIERS } from '../../lib/utils/constants';

import {
	IconNavigationChevronArrowRightComponent,
	IconNavigationChevronArrowLeftComponent
} from '@vaa-component-lib/component.atom.icons';
import {
	TypographyComponent,
	TypographySize,
	TypographyVariant,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';
import {
	ButtonColour,
	ButtonComponent,
	ButtonSize
} from '@vaa-component-lib/component.atom.button';

import MonthViewComponent from '../date-picker-month-view/month-view.component';

import {
	DaysHeading,
	Pickers,
	DateCarousel,
	Slider,
	ScrollBarMobile
} from './date-picker.styles';

interface DatePickerComponentProps {
	onChange: (value: string[]) => void;
	onSelectionComplete?: (value: string[]) => void;
	values: string[];
	showDays?: boolean;
	selectionType?: 'range' | 'single';
	isVisible?: boolean;
	enabledDateRange?: string[];
	bookingType?: string;
}

const DatePickerComponent = ({
	selectionType = 'range',
	onChange,
	onSelectionComplete,
	enabledDateRange,
	bookingType,
	values = [],
	isVisible = false
}: DatePickerComponentProps) => {
	const isFromTablet = useMediaQuery(`(min-width: ${BreakPointToken.MdMin}px)`);

	const defaultStart = DateTime.now().startOf('month').plus({
		days: DATE_MODIFIERS.defaultStartDays
	});

	const enabledStartDate = enabledDateRange?.[0]
		? DateTime.fromISO(enabledDateRange[0]).startOf('month')
		: null;
	const enabledEndDate = enabledDateRange?.[1]
		? DateTime.fromISO(enabledDateRange[1]).endOf('month')
		: null;

	const isEnabledRangeInPast =
		enabledEndDate && enabledEndDate < DateTime.now().startOf('day');

	const start =
		enabledStartDate && enabledStartDate >= defaultStart
			? enabledStartDate
			: defaultStart;

	const defaultEnd = start.plus({ months: DATE_MODIFIERS.defaultEndMonths });

	const end = isEnabledRangeInPast ? defaultEnd : enabledEndDate || defaultEnd;

	const interval = Interval.fromDateTimes(start, end);
	const dates = interval.splitBy({ months: 1 }).map((d) => d);

	const [localValues, setLocalValues] = useState<string[]>(values);

	//Desktop Scrolling
	const carouselRef = useRef(null);
	const carouselMobileRef = useRef(null);
	const slider = carouselRef.current;

	const [isLeftCarouselBtnDisabled, setIsLeftCarouselBtnDisabled] =
		useState<boolean>(true);
	const [isRightCarouselBtnDisabled, setIsRightCarouselBtnDisabled] =
		useState<boolean>(false);

	useEffect(() => {
		const selectedStartMonth = slider?.querySelector('#active-month');
		setTimeout(() => {
			slider?.scrollTo({
				left: selectedStartMonth?.offsetLeft
			});
		}, 0);
	}, [carouselRef.current, !isVisible, isFromTablet]);

	useEffect(() => {
		const sliderMobile = carouselMobileRef.current;
		const selectedStartMonth = sliderMobile?.querySelector('#active-month');
		setTimeout(() => {
			sliderMobile?.scrollTo({
				top: selectedStartMonth?.offsetTop - 60
			});
		}, 0);
	}, [carouselMobileRef.current, !isVisible, !isFromTablet]);

	const handleCarouselScroll = useCallback(() => {
		if (!slider) return;
		const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

		setIsLeftCarouselBtnDisabled(slider.scrollLeft === 0);
		setIsRightCarouselBtnDisabled(slider.scrollLeft >= maxScrollLeft);
	}, [slider]);

	useEffect(() => {
		if (!slider) return;

		const selectedMonth = DateTime.fromISO(localValues[0]).startOf('month');
		const firstAvailableMonth = dates[0].start;
		const lastAvailableMonth = dates[dates.length - 1].start;

		setIsLeftCarouselBtnDisabled(selectedMonth <= firstAvailableMonth);
		setIsRightCarouselBtnDisabled(selectedMonth >= lastAvailableMonth);
	}, [localValues, dates, slider]);

	useEffect(() => {
		if (isFromTablet && isVisible) {
			if (!slider) {
				return;
			}

			const timeoutId = setTimeout(handleCarouselScroll, 200);
			slider.addEventListener('scroll', handleCarouselScroll);

			return () => {
				clearTimeout(timeoutId);
				slider.removeEventListener('scroll', handleCarouselScroll);
			};
		}
	}, [handleCarouselScroll, isFromTablet, isVisible]);

	const scrollToNext = (direction: string) => {
		const slider = carouselRef.current;
		const slideWidth = slider?.children[0].offsetWidth;
		const scrollDistance = direction === 'left' ? -slideWidth : slideWidth;
		setTimeout(() => {
			slider.scrollTo({
				left: slider.scrollLeft + scrollDistance,
				behavior: 'smooth'
			});
		}, 500);
	};
	const selectDates = (day: string) => {
		// aggregate values if range else send single value
		if (
			selectionType === 'range' &&
			localValues.length === 1 &&
			DateTime.fromISO(day).toISODate() >=
				DateTime.fromISO(localValues[0]).toISODate()
		)
			setLocalValues([...localValues, day]);
		else setLocalValues([day]);
	};

	useEffect(() => {
		// only emit change value when selection is complete
		if (!localValues.length) return;
		onChange(localValues);

		if (typeof onSelectionComplete === 'undefined') return;
		if (selectionType === 'range' && localValues.length !== 2) return;
		if (selectionType === 'single' && localValues.length !== 1) return;
		onSelectionComplete(localValues);
	}, [localValues]);

	useEffect(() => {
		if (!localValues.length) return;
		setLocalValues(
			selectionType === 'range' ? localValues : localValues.slice(0, 1)
		);
	}, [selectionType]);

	const months = (
		<>
			{dates.map((date: Interval, i: number) => {
				const isSelectedMonth = date.start.hasSame(
					DateTime.fromISO(localValues[0]),
					'month'
				);
				return (
					<MonthViewComponent
						bookingType={bookingType}
						enabledDateRange={enabledDateRange}
						key={i}
						showDays={isFromTablet}
						startDate={date.start.toISO()}
						selectedDateRange={localValues}
						onDaySelected={(date) => selectDates(date)}
						{...{ isVisible, ...(isSelectedMonth && { id: 'active-month' }) }}
					/>
				);
			})}
		</>
	);

	return isFromTablet ? (
		<div data-cy="date-picker-component-desktop">
			<DateCarousel>
				<ButtonComponent
					iconChild={<IconNavigationChevronArrowLeftComponent />}
					colour={ButtonColour.Contrast}
					size={ButtonSize.XSmall}
					onClick={() => scrollToNext('left')}
					disabled={isLeftCarouselBtnDisabled}
				/>
				<Slider tabIndex={-1} ref={carouselRef} data-cy="date-picker-slider">
					{months}
				</Slider>
				<ButtonComponent
					iconChild={<IconNavigationChevronArrowRightComponent />}
					colour={ButtonColour.Contrast}
					size={ButtonSize.XSmall}
					onClick={() => scrollToNext('right')}
					disabled={isRightCarouselBtnDisabled}
				/>
			</DateCarousel>
		</div>
	) : (
		<ScrollBarMobile
			data-cy="date-picker-component-mobile"
			ref={carouselMobileRef}>
			<DaysHeading data-cy="date-picker-mobile-heading">
				<TypographyComponent
					element="div"
					variant={TypographyVariant.Body}
					size={TypographySize.Xsm}
					weight={TypographyWeight.Regular}>
					<div>Mon</div>
					<div>Tue</div>
					<div>Wed</div>
					<div>Thu</div>
					<div>Fri</div>
					<div>Sat</div>
					<div>Sun</div>
				</TypographyComponent>
			</DaysHeading>
			<Pickers>{months}</Pickers>
		</ScrollBarMobile>
	);
};

export default DatePickerComponent;
